import React, { useState, useEffect } from 'react';
import AuthLayout from './AuthLayout';
import { supabase } from '../../lib/supabase';

import { User, Mail, Phone, Lock, UserPlus } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { detectRegion, getCurrencyInfo } from '../../lib/regions';

const SignupPage = ({ isDarkMode, setIsDarkMode }) => {
    const [fullName, setFullName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [region, setRegion] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        const fetchRegion = async () => {
            const detected = await detectRegion();
            setRegion(detected);
        };
        fetchRegion();
    }, []);

    const currency = getCurrencyInfo(region);

    const handleSignup = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            // Sign up the user with Supabase Auth
            const { data, error: signupError } = await supabase.auth.signUp({
                email,
                password,
                options: {
                    data: {
                        full_name: fullName,
                        phone: phone,
                        pricing_region: region
                    }
                }
            });

            if (signupError) throw signupError;

            if (data?.user) {
                // Navigate to OTP verification page
                navigate('/verify-otp', { state: { email } });
            }
        } catch (err) {
            setError(err.message || 'An error occurred during signup');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout
            title="Create Sanctuary"
            subtitle="Begin your sacred breathing"
            isDarkMode={isDarkMode}
            setIsDarkMode={setIsDarkMode}
        >
            <form onSubmit={handleSignup} className="space-y-4">
                <div>
                    <label className={`block text-[10px] font-black uppercase tracking-widest mb-1 shadow-sm ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                        Sacred Name
                    </label>
                    <div className="relative">
                        <input
                            type="text"
                            required
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            className={`w-full pl-10 pr-4 py-3 rounded-xl border transition-all focus:ring-2 outline-none font-bold text-sm ${isDarkMode
                                ? 'bg-cosmic-800 border-white/10 text-white focus:ring-mystic-teal/50 focus:border-mystic-teal/50'
                                : 'bg-slate-50 border-slate-200 text-slate-900 focus:ring-blue-500/20'
                                }`}
                            placeholder="Full Name"
                        />
                        <User className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-gray-600' : 'text-slate-400'}`} />
                    </div>
                </div>

                <div>
                    <label className={`block text-[10px] font-black uppercase tracking-widest mb-1 shadow-sm ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                        Contact Email
                    </label>
                    <div className="relative">
                        <input
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className={`w-full pl-10 pr-4 py-3 rounded-xl border transition-all focus:ring-2 outline-none font-bold text-sm ${isDarkMode
                                ? 'bg-cosmic-800 border-white/10 text-white focus:ring-mystic-teal/50 focus:border-mystic-teal/50'
                                : 'bg-slate-50 border-slate-200 text-slate-900 focus:ring-blue-500/20'
                                }`}
                            placeholder="name@example.com"
                        />
                        <Mail className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-gray-600' : 'text-slate-400'}`} />
                    </div>
                </div>

                <div>
                    <label className={`block text-[10px] font-black uppercase tracking-widest mb-1 shadow-sm ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                        Mobile Number
                    </label>
                    <div className="relative">
                        <input
                            type="tel"
                            required
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            className={`w-full pl-10 pr-4 py-3 rounded-xl border transition-all focus:ring-2 outline-none font-bold text-sm ${isDarkMode
                                ? 'bg-cosmic-800 border-white/10 text-white focus:ring-mystic-teal/50 focus:border-mystic-teal/50'
                                : 'bg-slate-50 border-slate-200 text-slate-900 focus:ring-blue-500/20'
                                }`}
                            placeholder="Phone Number"
                        />
                        <Phone className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-gray-600' : 'text-slate-400'}`} />
                    </div>
                </div>

                <div>
                    <label className={`block text-[10px] font-black uppercase tracking-widest mb-1 shadow-sm ${isDarkMode ? 'text-gray-400' : 'text-slate-500'}`}>
                        Secret Key
                    </label>
                    <div className="relative">
                        <input
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className={`w-full pl-10 pr-4 py-3 rounded-xl border transition-all focus:ring-2 outline-none font-bold text-sm ${isDarkMode
                                ? 'bg-cosmic-800 border-white/10 text-white focus:ring-mystic-teal/50 focus:border-mystic-teal/50'
                                : 'bg-slate-50 border-slate-200 text-slate-900 focus:ring-blue-500/20'
                                }`}
                            placeholder="••••••••"
                        />
                        <Lock className={`absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-gray-600' : 'text-slate-400'}`} />
                    </div>
                </div>

                {error && (
                    <div className="bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-black p-3 rounded-xl text-center uppercase tracking-tighter">
                        {error}
                    </div>
                )}

                <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-gradient-to-r from-blue-600 to-mystic-purple hover:scale-[1.02] active:scale-[0.98] text-white font-black py-4 rounded-xl shadow-lg shadow-mystic-purple/20 transition-all flex items-center justify-center space-x-2 disabled:opacity-50"
                >
                    <UserPlus className="w-5 h-5" />
                    <span>{loading ? 'Creating...' : 'Create Account'}</span>
                </button>

                <p className={`text-[10px] text-center font-bold px-4 ${isDarkMode ? 'text-gray-600' : 'text-slate-400'}`}>
                    By joining, you agree to begin your 60-day free trial. Regional pricing of {currency.symbol}{currency.price}/month applies thereafter.
                </p>
            </form>

            <div className={`mt-8 text-center ${isDarkMode ? 'text-gray-500' : 'text-slate-400'}`}>
                <p className="text-xs font-black uppercase tracking-widest">
                    Already a practitioner?{' '}
                    <Link to="/login" className="text-blue-500 hover:text-blue-400 transition-colors">
                        Sign In
                    </Link>
                </p>
            </div>
        </AuthLayout>
    );
};

export default SignupPage;
